import { SourceSystemResourceFilter } from './components/SourceSystemResourceFilter';
import { useMemo } from "react";
import { Filter } from "lucide-react";
import { DataTable } from "../../components/DataTable";
import { MultiSelectDropdown } from "../../components/MultiSelectDropdown";
import { TableToolbar } from "../../components/TableToolbar";
import { Button } from "../../components/ui/button";
import { AccessActionButtons } from "./components/AccessActionButtons";
import { AccessActionDialog } from "./AccessActionDialog";
import { AccountReviewHistoryDialog } from "./components/AccountReviewHistoryDialog";
import { PagChartDrilldownDrawer } from "./components/PagChartDrilldownDrawer";
import {
  buildAccountColumns,
  buildDrilldownColumns,
  buildDetailColumns,
} from "./helpers/pagInsightsColumns";
import {
  hasOpenReviewRequest,
  isAccountOverdue,
  isDateOverdue,
  OVERDUE_ROW_CLASS,
  resourceOptions,
  sourceSystemOptions,
} from "./helpers/pagHelpers";
import { useInsightsData } from "./hooks/useInsightsData";


export const AccessReview = () => {
  const d = useInsightsData({ accountsOnly: true });

  const columns = useMemo(
    () =>
      buildAccountColumns({
        allSelected: d.allSelected,
        someSelected: d.someSelected,
        selectedIds: d.selectedIds,
        selectableAccounts: d.selectableAccounts,
        toggleAll: d.toggleAll,
        toggleRow: d.toggleRow,
        openAccountDetails: d.openAccountDetails,
      }),
    [
      d.allSelected,
      d.someSelected,
      d.selectedIds,
      d.selectableAccounts,
      d.toggleAll,
      d.toggleRow,
      d.openAccountDetails,
    ],
  );

  const drilldownColumns = useMemo(
    () => buildDrilldownColumns(columns),
    [columns],
  );

  const detailColumns = useMemo(() => buildDetailColumns(), []);

  return (
    <div className="flex flex-col gap-5 pb-10">
      {/* ── Top Filters ──────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-card p-4">
        <SourceSystemResourceFilter
          sourceSystemFilters={d.sourceSystemFilters}
          sourceSystems={d.sourceSystems}
          resources={d.resources}
          onSourceSystemsChange={d.changeSourceSystems}
          onResourcesChange={d.setResources}
        />
        <div className="flex items-center gap-2">
          <Button
            onClick={() => {
              const idsToApply = d.stagedSourceSystemIds.length
                ? d.stagedSourceSystemIds
                : resourceOptions(d.sourceSystemFilters, d.sourceSystems)
                  .map((opt) => Number(opt.value))
                  .filter((id) => Number.isFinite(id) && id > 0);
              if (idsToApply.length) {
                d.setAppliedSourceSystemIds(idsToApply);
              }
              d.setPage(0);
              d.setScopeLoading(true);
              d.setReloadToken((token) => token + 1);
            }}
            disabled={d.scopeLoading || d.sourceSystems.length === 0 || d.resources.length === 0}
            variant="indigo"
            className="justify-center min-w-[92px] gap-1.5 px-5 font-semibold disabled:opacity-60"
          >
            <Filter className="size-3.5" />
            Filter
          </Button>
          <Button
            onClick={() => {
              d.changeSourceSystems([]);
              d.setResources([]);
              d.setAppliedSourceSystemIds([]);
              d.setPage(0);
              d.setScopeLoading(true);
              d.setReloadToken((token) => token + 1);
            }}
            disabled={d.scopeLoading}
            variant="outline"
            className="justify-center min-w-[92px] px-5 hover:border-indigo-400/60 hover:bg-muted/40 disabled:opacity-60"
          >
            Clear
          </Button>
        </div>
      </div>

      {/* ── Table Card ──────────────────────────────────────────────── */}
      <div className="flex flex-col gap-4 rounded-xl border border-border bg-card p-5">
        <div className="flex items-center justify-between gap-4">
          <h3 className="font-semibold text-foreground">Privileged Account Inventory</h3>
        </div>

        <TableToolbar
          className="max-h-fit"
          search={{
            value: d.search,
            onChange: d.setSearch,
            onSubmit: d.submitSearch,
            placeholder: "Search",
            maxLength: 80,
            ariaLabel: "Search privileged accounts",
          }}
          filters={[
            {
              key: "risk",
              leadingLabel: "Risk",
              multiple: true,
              values: d.riskFilters,
              onValuesChange: (values) => {
                d.setRiskFilters(values);
                d.setPage(0);
              },
              placeholder: "",
              options: d.riskFilterOptions,
            },
            {
              key: "groupName",
              leadingLabel: "Group",
              multiple: true,
              values: d.groupFilters,
              onValuesChange: (values) => {
                d.setGroupFilters(values);
                d.setPage(0);
              },
              placeholder: "",
              options: d.groupFilterOptions,
            },
            {
              key: "pamStatus",
              leadingLabel: "PAM Status",
              multiple: true,
              values: d.pamFilters,
              onValuesChange: (values) => {
                d.setPamFilters(values);
                d.setPage(0);
              },
              placeholder: "",
              options: d.pamFilterOptions.map((value) => ({ value, label: value })),
            },
            {
              key: "review",
              leadingLabel: "Review",
              multiple: true,
              values: d.reviewFilters,
              onValuesChange: (values) => {
                d.setReviewFilters(values);
                d.setPage(0);
              },
              placeholder: "",
              options: d.reviewFilterOptions,
            },
          ]}
          pageSize={d.pageSize}
          pageSizeLabel="Rows per page"
          onPageSizeChange={(size) => {
            d.setPageSize(size);
            d.setPage(0);
          }}
          actions={
            <AccessActionButtons
              selectedCount={d.selectedIds.length}
              onApprove={() => d.openAccessAction("review", d.selectedIds)}
              onDeny={() => d.openAccessAction("revoke", d.selectedIds)}
            />
          }
        />

        <div
          className={`transition-opacity ${d.accountsLoading && d.accounts.length > 0 ? "pointer-events-none opacity-60" : ""
            }`}
        >
          <DataTable
            columns={columns}
            data={d.visibleAccounts}
            keyField="accountName"
            loading={d.accountsLoading}
            page={d.page}
            pageSize={d.pageSize}
            totalItems={d.accountsTotal}
            onPageChange={d.setPage}
            sortKey={d.sortKey}
            sortDir={d.sortDir}
            onSort={d.handleSort}
            maxHeight="600px"
            itemLabel="accounts"
            rowClassName={(row) => (isAccountOverdue(row) ? OVERDUE_ROW_CLASS : "")}
            emptyMessage={
              d.appliedSourceSystemIds.length === 0
                ? "Select a source system and resource to view data."
                : "No Data Available"


            }
          />
        </div>
      </div>

      {/* ── Drilldown Drawer ────────────────────────────────────────── */}
      <PagChartDrilldownDrawer
        drilldown={d.drilldown}
        sourceSystemIds={d.appliedSourceSystemIds}
        columns={drilldownColumns}
        refreshToken={d.reloadToken}
        filterOptionsData={d.filterOptionsData}
        onRequestAction={d.openAccessAction}
        onClose={() => d.setDrilldown(null)}
      />

      {/* ── Action Dialog (Review / Revoke) ─────────────────────────── */}
      <AccessActionDialog
        action={d.accessAction}
        selectedCount={d.actionNames.length}
        submitting={d.submittingAction}
        onSubmit={(action, payload) => void d.submitAccessAction(action, payload)}
        onClose={() => d.setAccessAction(null)}
      />

      {/* ── Account Review History Dialog ───────────────────────────── */}
      <AccountReviewHistoryDialog
        account={d.detailAccount}
        data={d.detailData}
        loading={d.detailLoading}
        columns={detailColumns}
        onClose={() => d.setDetailAccount(null)}
      />
    </div>
  );
};
